import { useLocale } from "../use-locale";
import type { IDomainFeeCharge } from "~/domain/fee-charge";
import type { IDomainOrderStatus } from "~/domain/order-status";
import type { IDomainOrderingMode } from "~/domain/ordering-mode";
import type { IDomainOutletState } from "~/domain/outlet-state";
import type { IDomainPaymentMode } from "~/domain/payment-mode";
import type { IDomainUnsupportedOrderingMode } from "~/domain/unsupported-ordering-mode";
import type { IDomainPickupPackaging } from "~/domain/pickup-packaging";

export const useDomainLabel = () => {
  const { t } = useLocale();

  const outletStateMap: Record<IDomainOutletState, string> = {
    open: t("composables.use_domain_label.outlet_state.open"),
    closed: t("composables.use_domain_label.outlet_state.closed"),
    busy: t("composables.use_domain_label.outlet_state.open"),
    pre_order_only: t(
      "composables.use_domain_label.outlet_state.pre_order_only",
    ),
  };

  const outletStateLabel = (state: IDomainOutletState) => {
    return outletStateMap[state];
  };

  const orderingModeMap: Record<
    IDomainOrderingMode | IDomainUnsupportedOrderingMode,
    string
  > = {
    delivery: t("composables.use_domain_label.ordering_mode.delivery"),
    pickup: t("composables.use_domain_label.ordering_mode.pickup"),
    curbside: t("composables.use_domain_label.ordering_mode.curbside"),
    "dine-in": t("composables.use_domain_label.ordering_mode.dine_in"),
  };

  const orderingModeLabel = (
    mode: IDomainOrderingMode | IDomainUnsupportedOrderingMode,
  ) => {
    return orderingModeMap[mode];
  };

  const feeChargeMap: Record<IDomainFeeCharge["key"], string> = {
    bagFee: t("composables.use_domain_label.fee_charge.bag_fee"),
    serviceCharges: t(
      "composables.use_domain_label.fee_charge.service_charges",
    ),
  };

  const feeChargeLabel = (charge: IDomainFeeCharge["key"]) => {
    return feeChargeMap[charge];
  };

  const paymentModeMap: Record<IDomainPaymentMode, string> = {
    adyen: t("composables.use_domain_label.payment_mode.pay_online"),
    cash: t("composables.use_domain_label.payment_mode.cash"),
    card: t("composables.use_domain_label.payment_mode.card"),
    gift_card: t("composables.use_domain_label.payment_mode.gift_card"),
    cash_delivery: t(
      "composables.use_domain_label.payment_mode.cash_on_delivery",
    ),
    cash_on_delivery: t(
      "composables.use_domain_label.payment_mode.cash_on_delivery",
    ),
    card_delivery: t(
      "composables.use_domain_label.payment_mode.card_on_delivery",
    ),
    card_on_delivery: t(
      "composables.use_domain_label.payment_mode.card_on_delivery",
    ),
    card_online: t("composables.use_domain_label.payment_mode.pay_online"),
    online: t("composables.use_domain_label.payment_mode.pay_online"),
    invoice: t("composables.use_domain_label.payment_mode.invoice"),
    other: t("composables.use_domain_label.payment_mode.other"),
  };

  const paymentModeLabel = (mode: IDomainPaymentMode) => {
    return paymentModeMap[mode];
  };

  const orderStatusMap: Record<IDomainOrderStatus, string> = {
    placed: t("composables.use_domain_label.order_status.placed"),
    accepted: t("composables.use_domain_label.order_status.accepted"),
    scheduled: t("composables.use_domain_label.order_status.scheduled"),
    complete: t("composables.use_domain_label.order_status.complete"),
    rejected: t("composables.use_domain_label.order_status.rejected"),
    cancelled: t("composables.use_domain_label.order_status.cancelled"),
    ready: t("composables.use_domain_label.order_status.ready"),
    unpaid: t("composables.use_domain_label.order_status.unpaid"),
    created: t("composables.use_domain_label.order_status.created"),
  };

  const orderStatusLabel = (mode: IDomainOrderStatus) => {
    return orderStatusMap[mode];
  };

  const packagingLabelMap: Record<IDomainPickupPackaging, string> = {
    takeaway: t("composables.use_domain_label.packaging.takeaway"),
    eatin: t("composables.use_domain_label.packaging.eat_in"),
  };

  const packagingLabel = (packaging: IDomainPickupPackaging) => {
    return packagingLabelMap[packaging];
  };

  return {
    outletStateMap,
    outletStateLabel,
    orderingModeMap,
    orderingModeLabel,
    feeChargeMap,
    feeChargeLabel,
    paymentModeMap,
    paymentModeLabel,
    orderStatusMap,
    orderStatusLabel,
    packagingLabelMap,
    packagingLabel,
  };
};
